import { MessageDescriptor } from '@lingui/core';

// --- СИСТЕМНІ ТИПИ (Фракції та Стани) ---
export const FactionType = {
    Player: 'player',
    Enemy: 'enemy',
    Neutral: 'neutral',
} as const;
export type FactionType = (typeof FactionType)[keyof typeof FactionType];

export const UnitState = {
    Idle: 'idle',
    Moving: 'moving',
    Attacking: 'attacking',
    Dead: 'dead',
} as const;
export type UnitState = (typeof UnitState)[keyof typeof UnitState];


// --- СВІТ (Террейн) ---

export const TerrainType = {
    Obstacle: 'obstacle',
    Plains: 'plains',
    Hill: 'hill',
    Cliff: 'cliff',
} as const;
export type TerrainType = (typeof TerrainType)[keyof typeof TerrainType];

export interface TerrainDef {
  id: TerrainType;
  name: MessageDescriptor;
  // visuals
  color: string;
  symbol?: string;
  // generation
  canPatch: boolean;
  canBeSeed: boolean;
  // movement & combat
  moveCost: number;
  sprite: string;
  spriteCols: number;
  spriteRowY: number;
  textureSize: number;
  
  evasion?: number;  // plus or minus absolute number
  hitChance?: number; // plus or minus absolute number
  powerMult?: number; // plus or minus relative number where 1 = no change, 0.5 half damage, 1.2 - 20% more avg damage
  // rules
  blocksMovement?: boolean;
  blocksLineOfSight?: boolean;
}



export const ControlState = {
    Idle: 'idle',
    PlayerMoving: 'player_moving',
    PlayerAttacking: 'player_attacking',
    EnemyTurn: 'enemy_turn',
    SkillTargeting: 'skill_targeting',
    Initializing: 'initializing',
    Ended: 'ended',
} as const;
export type ControlState = (typeof ControlState)[keyof typeof ControlState];

export const BattleStatus = {
    Preparing: 'preparing',
    Paused: 'paused',
    Playing: 'playing',
    Victory: 'victory',
    Defeat: 'defeat'
} as const;
export type BattleStatus = (typeof BattleStatus)[keyof typeof BattleStatus];



export interface FactionDef {
  color: number;
}

export interface Hex {
  id: string;
  // Логічні координати
  q: number;
  r: number;
  
  // Піксельні координати (центр гекса)
  // Розраховуються один раз при генерації
  x: number;
  y: number;

  // Тип ландшафту
  terrain: TerrainType;

  // Стан гекса
  isCliff: boolean;    // Скеля (непрохідна межа)
  isObstacle: boolean; // Чи є тут камінь/дерево (може залежати від rng)
  
  // Посилання на об'єкт, що стоїть на гексі (опціонально)
  entityId?: string;   // ID юніта, який зараз займає цей гекс
};

export interface HexPoint { 
  x: number, 
  y: number 
}


export interface Skill {
  id: string;          // Унікальний ID: "shot", "double_shot"
  name: string;        // Відображувана назва
  sort: number;        // Порядок у панелі інтерфейсу (10, 20...)
  cost: number;        // Вартість у стаміні
  range: number;       // Дальність (1 = ближній бій, >1 = дальній)
  
  powerMult?: number;       // Потужність (відсоток від базового урону)
  hitChanceBonus?: number;   // Модифікатор влучання (може бути від'ємним, як -15)
  critChanceBonus?: number;   // Модифікатор шансу крита (може бути від'ємним, як -15)
  critMultBonus?: number;   // Модифікатор сили крита (може бути від'ємним, як -0.2)
  armorPenBonus?: number;  // Модифікатор пробиття броні (може бути від'ємним, як -0.2)
  radius?: number;      // Радіус ураження (0 = одна ціль, 1+ = AoE)
  
  // Додаткові поля на майбутнє
  description?: string; 
  icon?: string;       // Шлях до іконки в атласі
  iconCol: number;       // Шлях до іконки в атласі
  iconRow: number;       // Шлях до іконки в атласі
}

export interface SkillConfig extends Omit<Skill, 'name'> {
    name: MessageDescriptor;
}

export interface UnitStats {
  hp: number;
  stamina: number;
  speed: number;
  armor: number;
  evasion: number;
  armorPenetration: number;
  meleeSkill?: number;
  rangedSkill?: number;
  initiative: number;
  critChance: number;
  critMult: number;
  damageMin: number;
  damageMax: number;
}

//units
export interface UnitJsonConfig {
    id: string;
    unitName: MessageDescriptor;
    stats: UnitStats;
    skills: SkillConfig[];
    anchor: { x: number; y: number };
    badge: { x: number; y: number };
    unitPortrait: { row: number; col: number };
    // hitArea: number[];
};

export interface BattleUnit {
    unitId: string;      // Унікальний ID для конкретного стеку на полі
    unitName: string;      // Посилання на назву з JSON (наприклад, 'Archer')
    count: number;         // Кількість людей у стеку
    maxUnitHp: number;         // Максимальне HP стеку
    moveCostMin: number;         // переміщення по flat terrain
    currentUnitHp: number; // HP "першого" воїна в стеку 
    currentStamina: number;
    q: number;
    r: number;
    isDead: boolean;
    faction: FactionType;
    stats: UnitJsonConfig['stats']; // Копія статів для модифікаторів у бою
    skills: Skill[]; 
    opportunityAttackUsed?: boolean; // Tracks if unit used opportunity attack this round
    unitPortrait?: { row: number; col: number };
};

export type TurnPool = {
    id?: string;
    faction: FactionType;
    unitIds: string[];
    deferredUnitIds?: Set<string>;
};


export interface CombatResult {
    targetId: string;
    damage: number;
    isCrit: boolean;
    isMiss: boolean;
    finalDefenderHp: number;
    finalDefenderCount: number;
    isDead: boolean;
    details: string; // Для логів або дебагу
    status?: BattleStatus
}

export interface SkillExecutedEvent {
    attackerId: string;
    targetId: string;
    skillId?: string;
    result: CombatResult;
}

export interface ReachableNodesResult {
  reachableIds: string[];
  visitedMap: Map<string, number>;
}

export interface PathInfo {
  path: string[];
  lastStep: {id: string, terrain: TerrainType} | null;
  cost: number;
}

export interface BattleConfig {
  playerDmgMult: number;   // 1.4 = легко, 1.0 = норм
  enemyDmgMult: number;    // 0.7 = легко, 1.0 = норм
}

export type ZocCheckpoint = { x: number; y: number; attackerIds: string[] }

export interface OpportunityAttacker {
    attackerId: string;
    stamina: number;
    hit: boolean;
}

export interface PathSegment {
    pathInfo: PathInfo;
    attackers: OpportunityAttacker[];
}